<template>
    <div>
        <div
            class="col-xs-10 clickable skill-row tw-truncate"
            tabindex="0"
            @click="goToReview()"
            @keyup.enter="goToReview()"
        >
            <l-profile-picture :user="user" size="xm" />
            <span class="l-default-margin">
                <span class="skill-title fake-link">
                    {{ title }}
                </span>
                <br />
                <span class="color-grey">
                    {{ reviewCycle.name }}
                </span>
            </span>
        </div>

        <div class="col-xs-2 skill-row hidden-xs">
            <span>
                <span
                    v-b-popover.hover.top="{
                        content: $t(`reviews.list.labelDeadline`),
                    }"
                    class="color-grey"
                >
                    <i class="fal fa-calendar-times"></i>
                </span>
                <span class="l-default-margin-s">
                    {{ reviewCycle.deadline | moment("MMM DD, YYYY") }}
                </span>
            </span>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import LProfilePicture from "@/components/LProfilePicture";

export default {
    components: {
        LProfilePicture,
    },
    props: {
        reviewCycle: {
            type: Object,
            required: true,
        },
    },
    computed: {
        ...mapGetters({
            currentUser: "users/current/user",
        }),
        user() {
            switch (this.reviewCycle.type) {
                case "360ReviewsComplete":
                case "peerSelfNominationsPending":
                    return this.currentUser;

                case "managerReviewsComplete":
                    if (this.reviewCycle.reviewee._id !== this.currentUser._id) {
                        return this.reviewCycle.reviewee;
                    }
                    return this.currentUser;

                default:
                    return this.reviewCycle.reviewee;
            }
        },
        title() {
            switch (this.reviewCycle.type) {
                case "360ReviewsComplete":
                    return this.$t(`reviews.list.receivedPeerReviews`);

                case "directReportReviewsPending":
                    return this.$t(`reviews.list.toDoDirectReportReviewsPending`, {
                        firstname: this.reviewCycle.reviewee.firstname,
                        lastname: this.reviewCycle.reviewee.lastname,
                    });

                case "directReportReviewsComplete":
                    return this.$t(`reviews.list.doneDirectReportReviews`, {
                        firstname: this.reviewCycle.reviewee.firstname,
                        lastname: this.reviewCycle.reviewee.lastname,
                    });

                case "managerReviewsPending":
                    return this.$t(`reviews.list.toDoManagerReviewsPending`, {
                        firstname: this.reviewCycle.reviewee.firstname,
                        lastname: this.reviewCycle.reviewee.lastname,
                    });

                case "additionalManagerReviewsPending":
                    return this.$t(`reviews.list.toDoManagerReviewsPending`, {
                        firstname: this.reviewCycle.reviewee.firstname,
                        lastname: this.reviewCycle.reviewee.lastname,
                    });

                case "managerReviewsComplete":
                    if (this.reviewCycle.reviewee._id !== this.currentUser._id) {
                        return this.$t(`reviews.list.doneManagerReviews`, {
                            firstname: this.reviewCycle.reviewee.firstname,
                            lastname: this.reviewCycle.reviewee.lastname,
                        });
                    }
                    return this.$t(`reviews.list.receivedManagerReviews`);

                case "additionalManagerReviewsComplete":
                    return this.$t(`reviews.list.doneManagerReviews`, {
                        firstname: this.reviewCycle.reviewee.firstname,
                        lastname: this.reviewCycle.reviewee.lastname,
                    });

                case "managerReviewsPendingShare":
                    return this.$t(`reviews.list.toDoManagerReviewsPendingShare`, {
                        firstname: this.reviewCycle.reviewee.firstname,
                        lastname: this.reviewCycle.reviewee.lastname,
                    });

                case "managerConversationsPending":
                    return this.$t(`reviews.list.toDoManagerConversationsPending`, {
                        firstname: this.reviewCycle.reviewee.firstname,
                        lastname: this.reviewCycle.reviewee.lastname,
                    });

                case "peerSelfNominationsPending":
                    return this.$t(`reviews.list.toDoPeerSelfNominationsPending`);

                case "peerNominationsPending":
                    return (
                        this.currentUser?.teamSettings?.customWording?.peerNominationsPending ??
                        this.$t(`reviews.list.toDoPeerNominationsPending`)
                    );

                case "peerApprovalPending":
                    return this.$t(`reviews.list.toDoPeerApprovalsPending`, {
                        firstname: this.reviewCycle.reviewee.firstname,
                        lastname: this.reviewCycle.reviewee.lastname,
                    });

                case "peerApprovalDone":
                    return this.$t(`reviews.list.donePeerApprovals`, {
                        firstname: this.reviewCycle.reviewee.firstname,
                        lastname: this.reviewCycle.reviewee.lastname,
                    });

                case "peerNominationsDone":
                    return this.$t(`reviews.list.donePeerNominations`);

                case "peerNominationsPendingEdit":
                    return this.$t(`reviews.list.donePeerNominations`);

                case "peerReviewsPending":
                    return this.$t(`reviews.list.toDoPeerReviewsPending`, {
                        firstname: this.reviewCycle.reviewee.firstname,
                        lastname: this.reviewCycle.reviewee.lastname,
                    });

                case "peerReviewsComplete":
                    return this.$t(`reviews.list.donePeerReviews`, {
                        firstname: this.reviewCycle.reviewee.firstname,
                        lastname: this.reviewCycle.reviewee.lastname,
                    });

                case "revieweeConversationsPending":
                    return this.$t(`reviews.list.toDoRevieweeConversationsPending`);

                case "selfEvaluationsPending":
                    return this.$t(`reviews.list.toDoSelfEvaluationsPending`);

                case "selfEvaluationsComplete":
                    return this.$t(`reviews.list.doneSelfEvaluations`);

                default:
                    return "";
            }
        },
    },
    methods: {
        goToReview() {
            let revieweeId;
            let role;
            let contributorId;
            let viewAs;
            let view;
            let entrypoint;

            switch (this.reviewCycle.type) {
                case "directReportReviewsPending":
                    revieweeId = this.reviewCycle.reviewee._id;
                    role = "directReport";
                    contributorId = this.reviewCycle.author;
                    viewAs = "directReport";
                    view = "overview";
                    entrypoint = "toDo";
                    break;

                case "peerNominationsPendingEdit":
                    revieweeId = this.reviewCycle.reviewee._id;
                    role = "reviewee";
                    view = "peerNominations";
                    contributorId = null;
                    viewAs = "reviewee";
                    entrypoint = "given";
                    break;

                case "selfEvaluationsComplete":
                    revieweeId = this.reviewCycle.reviewee._id;
                    role = "reviewee";
                    view = "overview";
                    contributorId = this.reviewCycle.author;
                    viewAs = "reviewee";
                    entrypoint = "given";
                    break;

                case "peerReviewsComplete":
                    revieweeId = this.reviewCycle.reviewee._id;
                    role = "peer";
                    view = "overview";
                    contributorId = this.reviewCycle.author;
                    viewAs = "peer";
                    entrypoint = "given";
                    break;

                case "directReportReviewsComplete":
                    revieweeId = this.reviewCycle.reviewee._id;
                    role = "directReport";
                    view = "overview";
                    contributorId = this.reviewCycle.author;
                    viewAs = "directReport";
                    entrypoint = "given";
                    break;

                case "managerReviewsComplete":
                    if (this.reviewCycle.reviewee._id !== this.currentUser._id) {
                        revieweeId = this.reviewCycle.reviewee._id;
                        role = "manager";
                        view = "overview";
                        contributorId = this.reviewCycle.author;
                        viewAs = "manager";
                        entrypoint = "given";
                    } else {
                        revieweeId = this.reviewCycle.reviewee._id;
                        role = "reviewee";
                        view = "overview";
                        contributorId = this.reviewCycle.author._id;
                        viewAs = "reviewee";
                        entrypoint = "received";
                    }
                    break;

                case "additionalManagerReviewsComplete":
                    revieweeId = this.reviewCycle.reviewee._id;
                    role = "additionalManager";
                    view = "overview";
                    contributorId = this.reviewCycle.author;
                    viewAs = "additionalManager";
                    entrypoint = "given";
                    break;

                case "peerApprovalDone":
                    revieweeId = this.reviewCycle.reviewee._id;
                    role = "manager";
                    view = "peerNominations";
                    contributorId = null;
                    viewAs = "manager";
                    entrypoint = "given";
                    break;

                case "peerNominationsDone":
                    revieweeId = this.reviewCycle.reviewee._id;
                    role = "reviewee";
                    view = "peerNominations";
                    contributorId = null;
                    viewAs = "reviewee";
                    entrypoint = "given";
                    break;

                case "360ReviewsComplete":
                    revieweeId = this.reviewCycle.reviewee._id;
                    role = "reviewee";
                    view = "overview";
                    contributorId = "none";
                    viewAs = "reviewee";
                    entrypoint = "received";
                    break;

                case "peerSelfNominationsPending":
                    revieweeId = null;
                    role = "selfNomination";
                    view = "peerSelfNominations";
                    contributorId = null;
                    viewAs = "selfNomination";
                    entrypoint = "toDo";
                    break;

                case "peerNominationsPending":
                    revieweeId = this.reviewCycle.reviewee._id;
                    role = "reviewee";
                    view = "peerNominations";
                    contributorId = null;
                    viewAs = "reviewee";
                    entrypoint = "toDo";
                    break;

                case "peerApprovalPending":
                    revieweeId = this.reviewCycle.reviewee._id;
                    role = "manager";
                    view = "peerNominations";
                    contributorId = null;
                    viewAs = "manager";
                    entrypoint = "toDo";
                    break;

                case "peerReviewsPending":
                    revieweeId = this.reviewCycle.reviewee._id;
                    role = "peer";
                    view = "overview";
                    contributorId = this.reviewCycle.author;
                    viewAs = "peer";
                    entrypoint = "toDo";
                    break;

                case "managerReviewsPending":
                    revieweeId = this.reviewCycle.reviewee._id;
                    role = "manager";
                    view = "overview";
                    contributorId = this.reviewCycle.author;
                    viewAs = "manager";
                    entrypoint = "toDo";
                    break;

                case "additionalManagerReviewsPending":
                    revieweeId = this.reviewCycle.reviewee._id;
                    role = "additionalManager";
                    view = "overview";
                    contributorId = this.reviewCycle.author;
                    viewAs = "additionalManager";
                    entrypoint = "toDo";
                    break;

                case "managerReviewsPendingShare":
                    revieweeId = this.reviewCycle.reviewee._id;
                    role = "manager";
                    view = "overview";
                    contributorId = this.reviewCycle.author;
                    viewAs = "manager";
                    entrypoint = "toDo";
                    break;

                case "selfEvaluationsPending":
                    revieweeId = this.reviewCycle.reviewee._id;
                    role = "reviewee";
                    view = "overview";
                    contributorId = this.reviewCycle.reviewee._id;
                    viewAs = "reviewee";
                    entrypoint = "toDo";
                    break;

                case "revieweeConversationsPending":
                    revieweeId = this.reviewCycle.reviewee._id;
                    role = "reviewee";
                    view = "overview";
                    contributorId = this.reviewCycle.reviewee._id;
                    viewAs = "reviewee";
                    entrypoint = "toDo";
                    break;

                case "managerConversationsPending":
                    revieweeId = this.reviewCycle.reviewee._id;
                    role = "manager";
                    view = "overview";
                    contributorId = this.reviewCycle.author;
                    viewAs = "manager";
                    entrypoint = "toDo";
                    break;
            }

            this.$router.push(
                `/reviews/update/${this.reviewCycle._id}/reviewee/${revieweeId}/${role}/${contributorId}/viewAs/${viewAs}/${view}/${entrypoint}`,
            );
        },
    },
};
</script>
